import React, { useContext, useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import { MY_ASSIGNMENTS_QUERY, MY_ASSIGNED_LESSONS_QUERY, GET_MEETING_WITH_CONTENT_QUERY, REVIEW_SUBMISSION_MUTATION, UNASSIGN_LESSON_MUTATION } from '../query/gqlQueries.js'
import styles from './AddProjectDialog.module.scss'
import StudentLessonView from '../UI/StudentLessonView'
import CloseIcon from '@mui/icons-material/Close'

// Homework, both ways round:
//   My homework  — lessons assigned TO me; open one, do it, record the speaking answers.
//   My students  — lessons I assigned; read each student's answers, leave feedback.
const STATUS = { assigned: ['To do', '#fbbf24'], submitted: ['Submitted', '#60a5fa'], reviewed: ['Reviewed', '#6ee7b7'] }
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '')
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '')
const card = { background: '#141418', border: '1px solid #2a2a33', borderRadius: 12, padding: '0.75rem 0.9rem' }
const btn = { background: '#1b1b21', color: '#e8e8ec', border: '1px solid #2a2a33', borderRadius: 9, padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem' }
const primary = { ...btn, background: '#34d399', color: '#06281d', border: 'none', fontWeight: 700 }

const Chip = ({ status }) => { const [label, color] = STATUS[status] || STATUS.assigned; return <span style={{ color, border: `1px solid ${color}55`, background: `${color}18`, borderRadius: 999, padding: '0.12rem 0.55rem', fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span> }

export default function HomeworkDialog({ voiceSpeed }) {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)
  const isOpen = dialogStates['dialogHomework']
  const [tab, setTab] = useState('mine')
  const [lesson, setLesson] = useState(null) // { lesson, meetingId } → StudentLessonView
  const [openErr, setOpenErr] = useState('')

  const mine = useQuery(MY_ASSIGNMENTS_QUERY, { variables: { username }, skip: !isOpen || !username, fetchPolicy: 'network-only', errorPolicy: 'all' })
  const teach = useQuery(MY_ASSIGNED_LESSONS_QUERY, { variables: { username }, skip: !isOpen || !username, fetchPolicy: 'network-only', errorPolicy: 'all' })
  const [fetchMeeting] = useLazyQuery(GET_MEETING_WITH_CONTENT_QUERY, { fetchPolicy: 'network-only' })

  if (!isOpen) return null
  const close = () => closeDialog('dialogHomework')
  const backendOld = /Cannot query field|Unknown type/i.test((mine.error && mine.error.message) || '')

  const openLesson = async (meetingId) => {
    setOpenErr('')
    try {
      const r = await fetchMeeting({ variables: { identity: meetingId } })
      const raw = r.data?.getMeetingWithContent?.meeting?.properties?.lessonData
      const l = JSON.parse(raw || 'null')
      if (l && Array.isArray(l.sections)) setLesson({ lesson: l, meetingId })
      else setOpenErr('This lesson has no content yet.')
    } catch (e) { setOpenErr(e.message) }
  }

  if (lesson) {
    return ReactDOM.createPortal(
      <StudentLessonView lesson={lesson.lesson} meetingId={lesson.meetingId} voiceSpeed={voiceSpeed} onClose={() => { setLesson(null); mine.refetch(); teach.refetch() }} />,
      document.body
    )
  }

  const assignments = mine.data?.myAssignments || []
  const taught = teach.data?.myAssignedLessons || []
  const tabStyle = (t) => ({ ...btn, borderColor: tab === t ? '#34d399' : '#2a2a33', color: tab === t ? '#6ee7b7' : '#8a8a95' })

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={close}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()} style={{ maxWidth: '680px' }}>
        <button className={styles.closeBtn} onClick={close}><CloseIcon /></button>
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Lessons</div>
            <h2 className={styles.title}>Homework</h2>
          </div>
        </div>
        <div className={styles.container}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.9rem' }}>
            <button style={tabStyle('mine')} onClick={() => setTab('mine')}>My homework{assignments.length ? ` (${assignments.filter(a => a.status === 'assigned').length} to do)` : ''}</button>
            <button style={tabStyle('teach')} onClick={() => setTab('teach')}>My students{taught.length ? ` (${taught.length})` : ''}</button>
          </div>

          {backendOld && <div style={{ color: '#fbbf24', marginBottom: '0.8rem' }}>The GraphQL server hasn't been redeployed with the homework schema yet.</div>}
          {openErr && <div style={{ color: '#f87171', marginBottom: '0.6rem' }}>{openErr}</div>}

          {tab === 'mine' && (
            mine.loading ? <div style={{ color: '#8a8a95' }}>Loading…</div>
              : assignments.length === 0 ? <div style={{ color: '#8a8a95', padding: '1rem 0', textAlign: 'center' }}>Nothing assigned to you yet.</div>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
                  {assignments.map(a => (
                    <div key={a.meetingId} style={{ ...card, display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ color: '#e8e8ec', fontWeight: 600 }}>{a.title}</div>
                        <div style={{ color: '#8a8a95', fontSize: '0.82rem' }}>from <b style={{ color: '#c3c3cc' }}>{a.teacher}</b>{a.due ? ` · due ${fmtDate(a.due)}` : ''}{a.submission?.updatedAt ? ` · saved ${fmtTime(a.submission.updatedAt)}` : ''}</div>
                        {a.submission?.comment && <div style={{ marginTop: '0.35rem', color: '#fbbf24', fontSize: '0.85rem' }}>💬 {a.submission.comment}</div>}
                      </div>
                      <Chip status={a.status} />
                      <button style={primary} onClick={() => openLesson(a.meetingId)}>{a.status === 'assigned' ? 'Start' : 'Open'}</button>
                    </div>
                  ))}
                </div>
          )}

          {tab === 'teach' && (
            teach.loading ? <div style={{ color: '#8a8a95' }}>Loading…</div>
              : taught.length === 0 ? <div style={{ color: '#8a8a95', padding: '1rem 0', textAlign: 'center' }}>You haven't assigned any lessons. Open a lesson in <em>Meetings</em> and press <b>Assign as homework</b>.</div>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', maxHeight: '60vh', overflowY: 'auto' }}>
                  {taught.map(t => (
                    <div key={t.meetingId} style={card}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.6rem', alignItems: 'baseline', marginBottom: '0.4rem' }}>
                        <div style={{ color: '#e8e8ec', fontWeight: 600 }}>{t.title}</div>
                        <div style={{ color: '#8a8a95', fontSize: '0.8rem' }}>{t.due ? `due ${fmtDate(t.due)} · ` : ''}{t.students.filter(s => s.status !== 'assigned').length}/{t.students.length} submitted</div>
                      </div>
                      {t.students.map(s => <StudentRow key={s.student} row={s} meetingId={t.meetingId} username={username} onChanged={() => teach.refetch()} />)}
                    </div>
                  ))}
                </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

function StudentRow({ row, meetingId, username, onChanged }) {
  const [open, setOpen] = useState(false)
  const [comment, setComment] = useState(row.submission?.comment || '')
  const [msg, setMsg] = useState('')
  const [review, { loading }] = useMutation(REVIEW_SUBMISSION_MUTATION)
  const [unassign] = useMutation(UNASSIGN_LESSON_MUTATION)
  const sub = row.submission
  const send = async () => {
    if (!sub) return
    setMsg('')
    try { await review({ variables: { username, submissionId: sub.id, comment } }); setMsg('Feedback sent ✓'); onChanged() }
    catch (e) { setMsg(e.message) }
  }
  const remove = async () => {
    if (!window.confirm(`Remove this homework for ${row.student}?`)) return
    try { await unassign({ variables: { username, meetingId, student: row.student } }); onChanged() } catch (e) { setMsg(e.message) }
  }
  return (
    <div style={{ borderTop: '1px solid #1f1f26', padding: '0.5rem 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span style={{ color: '#e8e8ec', flex: 1 }}>{row.student}</span>
        {sub?.updatedAt && <span style={{ color: '#8a8a95', fontSize: '0.78rem' }}>{fmtTime(sub.updatedAt)}</span>}
        <Chip status={row.status} />
        <span style={{ color: '#8a8a95' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {!sub ? <div style={{ color: '#8a8a95', fontSize: '0.85rem' }}>No answers yet.</div>
            : (sub.answers || []).map(a => (
              <div key={a.index} style={{ background: '#0d0d0f', border: '1px solid #2a2a33', borderRadius: 10, padding: '0.5rem 0.7rem' }}>
                <div style={{ color: '#8a8a95', fontSize: '0.8rem' }}>{a.index + 1}. {a.prompt}</div>
                <div style={{ color: a.text ? '#e8e8ec' : '#555560', marginTop: '0.2rem' }}>{a.text || '— no answer —'}</div>
              </div>
            ))}
          {sub && (
            <>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} placeholder="Feedback for the student…"
                style={{ width: '100%', boxSizing: 'border-box', background: '#0d0d0f', border: '1px solid #2a2a33', borderRadius: 10, color: '#e8e8ec', padding: '0.5rem 0.7rem', fontFamily: 'inherit', fontSize: '0.9rem' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
                <button style={primary} onClick={send} disabled={loading || !comment.trim()}>{loading ? 'Sending…' : 'Send feedback'}</button>
                <button style={btn} onClick={remove}>Remove homework</button>
                {msg && <span style={{ color: msg.endsWith('✓') ? '#6ee7b7' : '#f87171', fontSize: '0.85rem' }}>{msg}</span>}
              </div>
            </>
          )}
          {!sub && <button style={btn} onClick={remove}>Remove homework</button>}
        </div>
      )}
    </div>
  )
}
